import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import type { IAdminGateway } from '@/domain/accessGateway';
import { backoffMs, MAX_ATTEMPTS } from '@/domain/backoff';
import {
  ConflictError,
  DomainError,
  ForbiddenError,
  NotFoundError,
  ServerError,
} from '@/domain/errors';
import {
  sharesForSession,
  type ProjectEvent,
  type Role,
  type ScheduledSession,
} from '@/domain/project';
import { DEFAULT_NUMBERING, type Court, type NumberingMode } from '@/domain/session';
import type { IDomStorage } from '@/services/DomStorage';
import type { IHasher } from '@/services/Hasher';
import type { IIdGen } from '@/services/IdGen';
import type { ILogger } from '@/services/Logger';
import type { ISleeper } from '@/services/Sleeper';

// The admin ViewModel behind the equipment-room TV: schedule the day's sessions, then mint the six
// codes a session needs. The bearer keys are born here and live only here; the project aggregate
// gets their hashes and nothing else (docs/sync.md).
//
// "Live only here" is literal and it is the reason `_keys` is a plain Map and not persisted: lose
// the admin device and the codes are re-minted, not recovered. A key that reached storage or an
// event would be a key handed to whoever reads it.

// The six QRs on the wall, in the order the TV renders them. Four coach codes, one per court, then
// the mover and the front desk.
export type CodeSlot = Court | 'mover' | 'frontdesk';

export const CODE_SLOTS = [
  'white',
  'red',
  'purple',
  'gold',
  'mover',
  'frontdesk',
] as const satisfies readonly CodeSlot[];

// What each slot grants. The court is set on the four coach codes and on nothing else: court
// identity comes from the code, not from a picker (docs/access.md).
const GRANTS: Record<CodeSlot, { role: Role; court: Court | undefined }> = {
  white: { role: 'coach', court: 'white' },
  red: { role: 'coach', court: 'red' },
  purple: { role: 'coach', court: 'purple' },
  gold: { role: 'coach', court: 'gold' },
  mover: { role: 'mover', court: undefined },
  frontdesk: { role: 'frontdesk', court: undefined },
};

// Sigma 2.5 is the scout's starting value. It is an event and not a constant so the tick stays
// reproducible from the log, and so the admin can turn the knob live on the day.
const INITIAL_SIGMA = 2.5;

// What a join URL needs beyond the keys. Neither is a store dependency: the origin is the
// platform's (a view layer that reads `window` directly is INVARIANTS rule 12), and the project id
// belongs to whoever opened the project.
export interface JoinLink {
  readonly origin: string;
  readonly projectId: string;
}

export class AdminAccessStore {
  // Every bearer key this device has minted, by session and slot. Never logged, never written into
  // an event — the aggregate gets hashes only. Observable, because the TV renders off it: a re-mint
  // has to repaint the six QRs rather than leave the wall showing codes the registry has just
  // disabled.
  //
  // Mirrored into THIS device's local storage, and nowhere else, because a reload of the admin tab
  // used to blank the TV and force a mid-session re-mint (hit on the first real manual test). That
  // is a narrower exposure than it sounds: the same keys are rendered as QR codes on a wall all
  // day, so the laptop's own storage adds no reader the TV has not already got. The line that must
  // hold is the original one — no key in an event, a log, or anything that syncs.
  private readonly _keys = new Map<string, string>();
  private _projectViewerKey: string | undefined;
  private _busy = false;
  private _lastError: string | undefined;

  constructor(
    private readonly gateway: IAdminGateway,
    private readonly hasher: IHasher,
    private readonly idGen: IIdGen,
    private readonly logger: ILogger,
    // Optional, and last, so the four-argument construction the contract pins still compiles. With
    // no sleeper the ladder retries immediately, which is what a test wants; production passes the
    // real one so conflicting devices decorrelate.
    private readonly sleeper?: ISleeper,
    // Appended-optional like the sleeper: the mirror that keeps the TV alive across a reload.
    private readonly storage?: IDomStorage,
  ) {
    makeAutoObservable(this);
  }

  // The day's run list, straight off the project fold. The TV picks which session it is showing
  // from this.
  get sessions(): readonly ScheduledSession[] {
    return this.gateway.projectState.sessions;
  }

  // One write at a time from the admin laptop. Minting is fourteen writes and a half-finished
  // second run would interleave disables and mints across two ladders.
  get busy(): boolean {
    return this._busy;
  }

  get lastError(): string | undefined {
    return this._lastError;
  }

  // Writes `sessionScheduled` on the project, then the creating `thresholdSet` on the new session
  // aggregate. That second write is what makes this device the session's owner, and therefore the
  // only device that can mint its codes (docs/sync.md). Order matters: a project ladder that
  // exhausts leaves no session aggregate behind at all.
  //
  // The numbering mode is written EXPLICITLY, including when it is the default. A fold's zero value
  // is retroactive: it applies to every session that ever existed, replayed on whatever build is
  // running, so a day that ran one way would come back the other way the moment somebody changed
  // the constant. An event makes each session's mode reproducible from its own log. The zero value
  // stays and carries the goal's intent for anything created outside this path (docs/roster.md).
  //
  // The counter-argument, which lost: nothing reads the mode after the day, so replaying a finished
  // session with the wrong one changes nothing anybody sees. True today, and it is an argument from
  // the current list of readers, which is exactly the kind that rots.
  scheduleSession = async (
    name: string,
    startsAtMs: number,
    mode: NumberingMode = DEFAULT_NUMBERING,
  ): Promise<void> => {
    const sessionId = this.idGen.next();
    await this.guard(async () => {
      await this.writeProject({
        kind: 'sessionScheduled',
        payload: { sessionId, name, startsAtMs },
      });
      await this.gateway.executeSession(
        sessionId,
        { kind: 'thresholdSet', payload: { sigma: INITIAL_SIGMA } },
        { idempotencyKey: this.idGen.next() },
      );
      await this.gateway.executeSession(
        sessionId,
        { kind: 'numberingModeSet', payload: { mode } },
        { idempotencyKey: this.idGen.next() },
      );
    });
  };

  // Mints the six session codes and records their hashes. The project viewer key is minted once for
  // the whole day and reused by every QR: a fresh one per session would be a second key the earlier
  // QRs no longer carry.
  //
  // Re-minting a slot writes `shareLinkDisabled` for the old hash BEFORE the replacement lands, so
  // a device holding the old code is refused rather than left resolving to a role nobody meant it
  // to have.
  mintCodes = async (sessionId: string): Promise<void> => {
    await this.guard(async () => {
      if (this.viewerKey() === undefined) {
        const { shareKey } = await this.gateway.mintProjectViewerShare();
        this.rememberViewerKey(shareKey);
      }
      for (const slot of CODE_SLOTS) {
        await this.mintSlot(sessionId, slot);
      }
    });
  };

  // The two tick knobs, tunable on the day (docs/rotation-algorithm.md, "The sigma constant";
  // docs/rotation.md, "Balance"). Events and never local settings, or the tick stops being
  // reproducible from the log and two admin devices can disagree about what the hall should look
  // like. Both preconditions live on the aggregate, so a value that makes the arithmetic
  // meaningless is refused at write time rather than folded and ignored.
  setSigma = async (sessionId: string, sigma: number): Promise<void> => {
    await this.guard(
      () =>
        this.gateway.executeSession(
          sessionId,
          { kind: 'thresholdSet', payload: { sigma } },
          { idempotencyKey: this.idGen.next() },
        ),
      'session',
    );
  };

  // The third knob, and the only one that is not about the tick. Same ladder, same guard: the
  // desks in the hall read this off their own folds, so a mode that lives on one laptop would
  // leave the other three asking humans for numbers the club never allocated (docs/roster.md).
  setNumberingMode = async (sessionId: string, mode: NumberingMode): Promise<void> => {
    await this.guard(
      () =>
        this.gateway.executeSession(
          sessionId,
          { kind: 'numberingModeSet', payload: { mode } },
          { idempotencyKey: this.idGen.next() },
        ),
      'session',
    );
  };

  setTolerance = async (sessionId: string, tolerance: number): Promise<void> => {
    await this.guard(
      () =>
        this.gateway.executeSession(
          sessionId,
          { kind: 'rebalanceToleranceSet', payload: { tolerance } },
          { idempotencyKey: this.idGen.next() },
        ),
      'session',
    );
  };

  // Stop, straight through the same ladder. START is deliberately NOT here: it has to stamp the
  // admin device's clock onto the event so ticks run from the actual start, and the object that
  // owns the tick schedule is the one that should be writing it (src/services/TickEngine.ts).
  // The preconditions shipped in P1 (scheduled -> running -> stopped -> running) are what stop a
  // double tap counting twice.
  stopSession = async (sessionId: string): Promise<void> => {
    await this.guard(
      () =>
        this.gateway.executeSession(
          sessionId,
          { kind: 'sessionStopped', payload: {} },
          { idempotencyKey: this.idGen.next() },
        ),
      'session',
    );
  };

  // True once this device holds all six bearer keys for the session. The TV gates on it: joinUrls
  // has nothing to render before minting, and a store that guessed would put a QR on the wall that
  // decodes to the word "undefined".
  hasCodes = (sessionId: string): boolean =>
    this.viewerKey() !== undefined &&
    CODE_SLOTS.every((slot) => this.keyFor(sessionId, slot) !== undefined);

  // The six URLs the TV turns into QR images. Bearer keys ride in the fragment, never the path or
  // the query, and the role is never in the URL: the registry decides what a code grants, so a URL
  // that announced it would be a URL a curious coach could edit.
  joinUrls = (sessionId: string, link: JoinLink): Record<CodeSlot, string> => {
    const pk = this.viewerKey();
    if (pk === undefined || !this.hasCodes(sessionId)) {
      throw new NotFoundError(`minted codes for session ${sessionId}`);
    }
    const urls = {} as Record<CodeSlot, string>;
    for (const slot of CODE_SLOTS) {
      const sk = this.keyFor(sessionId, slot);
      // hasCodes already proved every slot is present; this is the compiler's share of it.
      if (sk === undefined) throw new NotFoundError(`the ${slot} code for session ${sessionId}`);
      urls[slot] = joinUrl(link, pk, sk);
    }
    return urls;
  };

  // The in-memory map first, the storage mirror second. The map is the observable half (a re-mint
  // repaints the TV through it); the mirror is what a reloaded tab reads, warmed back into the map
  // so the reactivity story is the same either way.
  private keyFor(sessionId: string, slot: CodeSlot): string | undefined {
    const cached = this._keys.get(keyOf(sessionId, slot));
    if (cached !== undefined) return cached;
    const stored = this.storage?.get(`grading-day.sk.${sessionId}.${slot}`);
    if (stored !== undefined) this._keys.set(keyOf(sessionId, slot), stored);
    return stored;
  }

  private viewerKey(): string | undefined {
    this._projectViewerKey ??= this.storage?.get('grading-day.pk');
    return this._projectViewerKey;
  }

  private rememberViewerKey(shareKey: string): void {
    this._projectViewerKey = shareKey;
    this.storage?.set('grading-day.pk', shareKey);
  }

  private mintSlot = async (sessionId: string, slot: CodeSlot): Promise<void> => {
    const grant = GRANTS[slot];
    // Read the registry fresh per slot: the disable for THIS slot has to be on the aggregate before
    // its replacement, and the mints for earlier slots have already moved the fold.
    const stale = sharesForSession(this.gateway.projectState, sessionId).filter(
      (s) => s.role === grant.role && s.court === grant.court,
    );
    for (const old of stale) {
      await this.writeProject({
        kind: 'shareLinkDisabled',
        payload: { codeHash: old.codeHash },
      });
    }
    // Mint ONCE, then ladder the write. A retry that re-minted would hand the wall a key whose hash
    // the retry never wrote, and the QR on the TV would stop matching the registry.
    const { shareKey } = await this.gateway.mintSessionShare(sessionId);
    const codeHash = await this.hasher.sha256Hex(shareKey);
    await this.writeProject({
      kind: 'shareLinkMinted',
      payload: {
        codeHash,
        sessionId,
        role: grant.role,
        ...(grant.court !== undefined ? { court: grant.court } : {}),
      },
    });
    this.rememberKey(sessionId, slot, shareKey);
  };

  // The OCC ladder for project writes. Same shape as the front desk's: one idempotency key held
  // across every attempt, so a write that landed but whose ack was lost resolves as a duplicate
  // rather than a second registry entry.
  private writeProject = async (event: ProjectEvent): Promise<void> => {
    const idempotencyKey = this.idGen.next();
    for (let attempt = 1; ; attempt++) {
      try {
        await this.gateway.executeProject(event, { idempotencyKey });
        return;
      } catch (cause) {
        const failure =
          cause instanceof DomainError
            ? cause
            : new ServerError(`admin: ${event.kind} failed`, { cause });
        // Only a conflict is worth re-sending: the payload means the same thing wherever it lands
        // and the aggregate has simply moved. Everything else a second identical write cannot fix.
        if (!(failure instanceof ConflictError) || attempt >= MAX_ATTEMPTS) {
          this.logger.warn(`admin: ${event.kind} failed on attempt ${attempt}`, failure.tag);
          throw failure;
        }
        this.logger.debug(`admin: OCC clash on ${event.kind}, retrying (attempt ${attempt})`);
        if (this.sleeper !== undefined)
          await this.sleeper.sleep(backoffMs(idempotencyKey, attempt));
      }
    }
  };

  // The busy flag and the error surface, around whatever the admin asked for. Rejections travel on:
  // the admin screen awaits these and shows what came back.
  //
  // `subject` is which aggregate the write was aimed at, and it exists for one line of the error
  // message. A knob turn is a `thresholdSet` on the SESSION; telling the admin another device is
  // writing the project sends them to look at the wrong thing, and the two conflicts have different
  // causes — a peer laptop scheduling, against a peer laptop turning the same knob.
  private guard = async (
    run: () => Promise<void>,
    subject: 'project' | 'session' = 'project',
  ): Promise<void> => {
    if (this._busy) throw new ServerError('The admin device is already writing. Wait for it.');
    this.startWrite();
    try {
      await run();
      this.clearError();
    } catch (cause) {
      const failure =
        cause instanceof DomainError ? cause : new ServerError('The write failed.', { cause });
      this.showError(adminMessage(failure, subject));
      throw failure;
    } finally {
      this.endWrite();
    }
  };

  private rememberKey(sessionId: string, slot: CodeSlot, shareKey: string): void {
    this._keys.set(keyOf(sessionId, slot), shareKey);
    this.storage?.set(`grading-day.sk.${sessionId}.${slot}`, shareKey);
  }

  private startWrite(): void {
    this._busy = true;
  }

  private endWrite(): void {
    this._busy = false;
  }

  private showError(message: string): void {
    this._lastError = message;
  }

  private clearError(): void {
    this._lastError = undefined;
  }
}

const keyOf = (sessionId: string, slot: CodeSlot): string => `${sessionId}:${slot}`;

// encodeURIComponent, not raw concatenation: a real share key is base64-ish and a `+` in a query
// string decodes back as a space. It is the identity function over the id and key charsets that do
// not need it, so the URL a human reads is unchanged in the ordinary case.
function joinUrl(link: JoinLink, pk: string, sk: string): string {
  const project = encodeURIComponent(link.projectId);
  return `${link.origin}/#/join?project=${project}&pk=${encodeURIComponent(pk)}&sk=${encodeURIComponent(sk)}`;
}

function adminMessage(failure: DomainError, subject: 'project' | 'session'): string {
  if (failure instanceof ConflictError) {
    return subject === 'session'
      ? 'Another device is writing this session. Try again.'
      : 'Another device is writing the project. Try again.';
  }
  // Minting needs Owner on the aggregate, and creating it is what confers ownership
  // (docs/sync.md). So this is the second laptop, or a device whose local store was cleared: the
  // day it is looking at was created somewhere else and nothing it does here will be allowed.
  // Naming that is the whole value — "that did not land" sends the admin to look at the wifi.
  if (failure instanceof ForbiddenError) {
    return 'This device does not own this grading day, so it cannot change it. Use the laptop that started the day.';
  }
  return `That did not land: ${failure.message}`;
}

export const TOKEN_AdminAccessStore = token<AdminAccessStore>('AdminAccessStore');
